/**
 * Simplified role-based permissions hook.
 * Uses effective_permissions from the backend (computed from role template + custom overrides).
 * 
 * Usage:
 *   const { can } = usePermissions();
 *   can('claims', 'delete')    // false for architect
 *   can('rfis', 'create')      // true for contractor
 *   can('rfis', 'respond')     // true for professional_team
 */
import { useAuth } from '@/context/AuthContext';

export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase()?.replace(/\s+/g, '_') || 'guest';
  const ep = user?.effective_permissions || {};

  /**
   * Core permission check: can(module, action)
   * @param {string} module - Module key matching backend: delay_events, notices, claims, rfis,
   *   site_instructions, programs, subcontractors, drawings, reports, irs, shop_drawings,
   *   meeting_minutes, procurement, variation_orders, tasks
   * @param {string} action - Action key: view, create, edit, delete, approve, submit, etc.
   * @returns {boolean}
   */
  const can = (module, action) => {
    if (role === 'admin') return true;
    const modulePerms = ep[module];
    if (!modulePerms) return false;
    if (modulePerms.hidden === true) return false;
    return modulePerms[action] === true;
  };

  /**
   * Check admin-level permissions (manage_users, manage_clients, etc.)
   */
  const canAdmin = (permission) => {
    if (role === 'admin') return true;
    return ep[permission] === true;
  };

  const isAdminBool = role === 'admin';
  // isAdmin as a function for backward compatibility with components calling isAdmin()
  const isAdmin = () => role === 'admin';

  // Legacy compatibility — maps old permission names to new can() calls
  const hasPermission = (permission) => {
    if (isAdminBool) return true;
    const legacyMap = {
      'can_create_events': ['delay_events', 'create'],
      'can_edit_events': ['delay_events', 'edit'],
      'can_delete_events': ['delay_events', 'delete'],
      'can_create_notices': ['notices', 'create'],
      'can_edit_notices': ['notices', 'edit'],
      'can_delete_notices': ['notices', 'delete'],
      'can_create_claims': ['claims', 'create'],
      'can_edit_claims': ['claims', 'edit'],
      'can_delete_claims': ['claims', 'delete'],
      'can_create_rfi': ['rfis', 'create'],
      'can_edit_rfi': ['rfis', 'edit'],
      'can_delete_rfi': ['rfis', 'delete'],
      'can_issue_rfi': ['rfis', 'issue'],
      'can_respond_rfi': ['rfis', 'respond'],
      'can_close_rfi': ['rfis', 'close'],
      'can_create_si': ['site_instructions', 'create'],
      'can_edit_si': ['site_instructions', 'edit'],
      'can_delete_si': ['site_instructions', 'delete'],
      'can_respond_si': ['site_instructions', 'respond'],
      'can_close_si': ['site_instructions', 'close'],
      'can_mark_ceased': ['delay_events', 'mark_ceased'],
      'can_convert_event_to_notice': ['delay_events', 'convert_to_notice'],
      'can_convert_notice_to_claim': ['notices', 'convert_to_claim'],
      'can_upload_costs': ['claims', 'edit'],
      'can_upload_programs': ['programs', 'upload'],
      'can_upload_drawings': ['drawings', 'upload'],
      'can_view_reports': ['reports', 'view'],
      'can_manage_teams': ['manage_professional_team', null],
      'can_manage_smtp': ['system_settings', null],
      'can_access_admin_dashboard': ['view_all_projects', null],
      'can_approve_requests': ['delay_events', 'approve'],
      'can_view_irs': ['irs', 'view'],
      'can_create_irs': ['irs', 'create'],
      'can_edit_irs': ['irs', 'edit'],
      'can_upload_irs': ['irs', 'upload'],
      'can_analyze_irs': ['irs', 'analyze'],
      'can_export_irs': ['irs', 'export'],
      'can_view_shop_drawings': ['shop_drawings', 'view'],
      'can_create_shop_drawings': ['shop_drawings', 'create'],
      'can_review_shop_drawings': ['shop_drawings', 'review'],
      'can_approve_shop_drawings': ['shop_drawings', 'approve'],
      'can_view_meeting_minutes': ['meeting_minutes', 'view'],
      'can_create_meeting_minutes': ['meeting_minutes', 'create'],
      'can_distribute_meeting_minutes': ['meeting_minutes', 'distribute'],
      'can_view_procurement': ['procurement', 'view'],
      'can_create_procurement': ['procurement', 'create'],
      'can_edit_procurement': ['procurement', 'edit'],
      'can_export_procurement': ['procurement', 'export'],
      'can_view_vo': ['variation_orders', 'view'],
      'can_create_vo': ['variation_orders', 'create'],
      'can_edit_vo': ['variation_orders', 'edit'],
      'can_approve_vo': ['variation_orders', 'approve'],
      'can_view_tasks': ['tasks', 'view'],
      'can_create_tasks': ['tasks', 'create'],
      'can_assign_tasks': ['tasks', 'assign'],
      'can_complete_tasks': ['tasks', 'complete'],
      // Document Control
      'can_view_document_control': ['document_control', 'view'],
      'can_upload_document_control': ['document_control', 'upload'],
      'can_verify_document_control': ['document_control', 'verify'],
      'can_reject_document_control': ['document_control', 'reject'],
      'can_edit_document_control': ['document_control', 'edit'],
      'can_manage_access_records': ['document_control', 'manage_access_records'],
      'can_mark_project_live': ['document_control', 'mark_project_live'],
    };
    const mapped = legacyMap[permission];
    if (!mapped) return false;
    if (mapped[1] === null) return canAdmin(mapped[0]);
    return can(mapped[0], mapped[1]);
  };

  return {
    role,
    user,
    isAdmin,
    can,
    canAdmin,
    hasPermission,
    // Legacy convenience methods (used throughout codebase)
    isAdminOrDirector: () => ['admin', 'director', 'project_director', 'contracts_director'].includes(role),
    canManage: () => ['admin', 'director', 'project_director', 'project_manager', 'contracts_manager', 'commercial_manager', 'cm'].includes(role),
    canDelete: () => isAdminBool,
    canAccessAdminDashboard: () => hasPermission('can_access_admin_dashboard'),
    canManageTeams: () => hasPermission('can_manage_teams'),
    canManageSMTP: () => hasPermission('can_manage_smtp'),
    canCreateEvents: () => can('delay_events', 'create') || can('delay_events', 'create_draft'),
    canEditEvents: () => can('delay_events', 'edit'),
    canCreateNotices: () => can('notices', 'create'),
    canEditNotices: () => can('notices', 'edit'),
    canCreateClaims: () => can('claims', 'create'),
    canEditClaims: () => can('claims', 'edit'),
    canCreateRFI: () => can('rfis', 'create'),
    canEditRFI: () => can('rfis', 'edit'),
    canIssueRFI: () => can('rfis', 'issue'),
    canRespondRFI: () => can('rfis', 'respond'),
    canCloseRFI: () => can('rfis', 'close'),
    canMarkCeased: () => can('delay_events', 'mark_ceased'),
    canCreateSI: () => can('site_instructions', 'create'),
    canEditSI: () => can('site_instructions', 'edit'),
    canUploadCosts: () => can('claims', 'edit'),
    canUploadPrograms: () => can('programs', 'upload'),
    canViewReports: () => can('reports', 'view'),
    canApproveRequests: () => can('delay_events', 'approve'),
    canCreateUsers: () => canAdmin('manage_users'),
    canCreateClients: () => canAdmin('manage_clients'),
    canCreateProjects: () => canAdmin('manage_projects'),
    canUploadDrawings: () => can('drawings', 'upload'),
    canConvertEventToNotice: () => can('delay_events', 'convert_to_notice'),
    canConvertNoticeToClaim: () => can('notices', 'convert_to_claim'),
    // Document Control permissions
    canViewDocumentControl: () => can('document_control', 'view'),
    canUploadDocuments: () => can('document_control', 'upload'),
    canVerifyDocuments: () => can('document_control', 'verify'),
    canRejectDocuments: () => can('document_control', 'reject'),
    canManageAccessRecords: () => can('document_control', 'manage_access_records'),
    canMarkProjectLive: () => can('document_control', 'mark_project_live'),
  };
}

export default usePermissions;
